import React, { useState } from 'react';
import { userAppContext } from '../../context/Context';
import { USER_ROLES_AWS_MAP } from '../../constants/userRoles';
import QueriesLimitTable from '../API/QueriesLimitTable';
import UpgradePlan from '../User/UpgradePlan';
import { useAcademicExpiration } from '../../hooks/useAcademicExpiration';
import AcademicExpirationInlineTag from '../Academic/AcademicExpirationInlineTag';
import AcademicExpirationModal from '../Academic/AcademicExpirationModal';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import ChangePassword from '../User/ChangePassword';
import ProfileAcademicTrialActions from './ProfileAcademicTrialActions';
interface ProfileDataProps {
  profileDataClass?: string;
}

const ProfileData: React.FC<ProfileDataProps> = ({ profileDataClass }) => {
  const { user, groups } = userAppContext();
  const [message] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [resetPassword, setResetPassword] = useState(false);

  // Academic expiration logic
  const {
    expirationStatus,
    showWarningModal,
    warningType,
    loading,
    isAcademic,
    shouldShowExpirationTag,
    tagInfo,
    handleRenewal,
    handleCancellation,
    dismissWarning,
    openModal,
    hasAppliedForRenewal,
    hasCancelledAccount,
    wasRenewalDenied,
    wasRenewalApproved,
    currentAcademicPlan,
  } = useAcademicExpiration(user, true);

  const handleReset = (evt: React.MouseEvent<HTMLAnchorElement>) => {
    evt.preventDefault();
    setResetPassword(true);
  };

  const userRole = USER_ROLES_AWS_MAP.get(groups?.[0]);
  const isExternal = user?.identities ? true : false;

  return (
    <>
      <div className={`${profileDataClass}`} id="profilelData">
        <h5>Account Information</h5>
        <div className="bg-white border rounded-xl p-4 mb-4">
          {user?.['custom:company'] ? (
            <>
              {' '}
              <p className="text-md text-uppercase font-semibold">Company: </p>
              <span className="text-sm">{user?.['custom:company']}</span>
              <div className="wrap-input100" />
            </>
          ) : null}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-md text-uppercase font-semibold mb-1">Email: </p>
              <span className="text-sm">{user?.email}</span>
            </div>
            {!isExternal && (
              <a
                className="text-sm font-semibold mb-2 cursor-pointer text-gray-500"
                href="#"
                onClick={handleReset}
              >
                Change Password
              </a>
            )}
          </div>
          {resetPassword && (
            <>
              <div className="wrap-input100 mt-4" />
              <ChangePassword onClose={() => setResetPassword(false)} />
            </>
          )}
        </div>

        <h5>Subscription: </h5>
        <div className="bg-white border rounded-xl p-4 mb-4">
          <div className="row justify-between items-center">
            <div>
              <p className="text-md text-uppercase font-semibold mb-0">Current plan: </p>
              <h6 className="text-uppercase font-bold text-blue text-2xl">{userRole}</h6>
            </div>
            <div className="flex items-end flex-col">
              {/* Academic Expiration Tag */}
              {isAcademic && shouldShowExpirationTag && (
                <AcademicExpirationInlineTag tagInfo={tagInfo} onClick={openModal} />
              )}
              {userRole !== 'ADVANCED' && <UpgradePlan />}
            </div>
          </div>
          <div className="wrap-input100 mt-4" />
          <span
            className="text-sm font-semibold mb-2 cursor-pointer text-gray-500"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <ChevronUpIcon className="w-4 h-4 inline-block ml-2 mr-2" />
            ) : (
              <ChevronDownIcon className="w-4 h-4 inline-block ml-2 mr-2" />
            )}
            Query limits:{' '}
          </span>
          <div className="overflow-hidden transition-all duration-300 mt-2">
            {isExpanded && <QueriesLimitTable />}
          </div>
        </div>
        <ProfileAcademicTrialActions />
      </div>

      {/* Academic Expiration Modal */}
      {isAcademic && (
        <AcademicExpirationModal
          open={showWarningModal}
          onClose={dismissWarning}
          warningType={warningType}
          expirationStatus={expirationStatus}
          onRenewal={handleRenewal}
          onCancellation={handleCancellation}
          loading={loading}
          hasAppliedForRenewal={hasAppliedForRenewal}
          hasCancelledAccount={hasCancelledAccount}
          wasRenewalDenied={wasRenewalDenied}
          wasRenewalApproved={wasRenewalApproved}
          currentAcademicPlan={currentAcademicPlan}
        />
      )}
    </>
  );
};

export default ProfileData;
